/**
 * Session state. The app must be fully usable signed OUT (plan + PDF without an
 * account, like the predecessor), so "no session" is a normal state, not an error.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import * as api from './api';

export interface SessionValue {
  user: api.Me | null;
  /** true until the first /auth/me answered */
  loading: boolean;
  /** the API could not be reached at all — stay in local-only mode */
  offline: boolean;
  /**
   * A call that needed the session came back 401 while this browser believed it
   * was signed in. `user` is deliberately **kept**: dropping it would empty the
   * editor's aircraft list, and with it every cell the aircraft profile fed. The
   * flag only raises the sign-in prompt (see `SessionExpiredPrompt`).
   */
  expired: boolean;
  dismissExpired(): void;
  signIn(email: string, password: string): Promise<void>;
  signUp(email: string, password: string, displayName?: string): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<api.Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [expired, setExpired] = useState(false);
  /* read from a subscription that outlives the render it was created in */
  const signedIn = useRef(false);
  signedIn.current = user !== null;

  const refresh = useCallback(async () => {
    try {
      const me = await api.currentUser();
      setUser(me);
      setOffline(false);
      if (me) setExpired(false);
    } catch (e) {
      setUser(null);
      setOffline(e instanceof api.ApiError && e.status === 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /* A 401 on an ordinary call is the only way the app learns that the httpOnly
     cookie is gone. Signed out, that is simply the normal state and says nothing. */
  useEffect(() => api.onSessionExpired(() => {
    if (signedIn.current) setExpired(true);
  }), []);

  const value = useMemo<SessionValue>(
    () => ({
      user,
      loading,
      offline,
      expired,
      dismissExpired() {
        setExpired(false);
      },
      async signIn(email, password) {
        setUser(await api.login(email, password));
        setOffline(false);
        setExpired(false);
      },
      async signUp(email, password, displayName) {
        setUser(await api.register(email, password, displayName));
        setOffline(false);
        setExpired(false);
      },
      async signOut() {
        try {
          await api.logout();
        } finally {
          setUser(null);
          setExpired(false);
        }
      },
      refresh,
    }),
    [user, loading, offline, expired, refresh],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const s = useContext(SessionContext);
  if (!s) throw new Error('useSession outside a <SessionProvider>');
  return s;
}
